import type { Navigator } from 'react-router-dom';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { parsePath, UNSAFE_NavigationContext } from 'react-router-dom';

/** A navigation the guard is holding. */
export type NavigationGuard = {
    /** The path the technician tried to open, or undefined while nothing is held. */
    heldPath: string | undefined;
    /** Let the held navigation through. */
    proceed: () => void;
    /** Drop the held navigation and stay. */
    stay: () => void;
};

/** A held navigation: where it goes and the call that replays it. */
type HeldNavigation = {
    /** The destination pathname. */
    path: string;
    /** Replay the navigation exactly as it was attempted. */
    retry: () => void;
};

/**
 * Hold in-app navigations for confirmation. A push or replace whose destination pathname the
 * predicate rejects is held instead of performed, one at a time, until it is either let through
 * with proceed or dropped with stay. A held navigation is also dropped as soon as the predicate
 * stops rejecting its destination. Query and hash never affect the decision.
 *
 * @param shouldHold - decides by destination pathname whether to hold a navigation; must be pure
 * @returns the held navigation with its resolutions
 */
export function useNavigationGuard(shouldHold: (pathname: string) => boolean): NavigationGuard {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const [held, setHeld] = useState<HeldNavigation>();
    const shouldHoldRef = useRef(shouldHold);

    useEffect(() => {
        shouldHoldRef.current = shouldHold;
        setHeld((current) => (current === undefined || shouldHold(current.path) ? current : undefined));
    }, [shouldHold]);

    useEffect(() => {
        const { push, replace } = navigator;

        /**
         * Wrap a navigator method so destinations the predicate rejects are held instead of performed.
         *
         * @param perform - the original navigator method
         * @returns the guarded method
         */
        function guarded(perform: Navigator['push']): Navigator['push'] {
            return (...args) => {
                const path = parsePath(navigator.createHref(args[0])).pathname ?? '';

                if (shouldHoldRef.current(path)) {
                    setHeld({ path, retry: () => perform(...args) });
                } else {
                    perform(...args);
                }
            };
        }

        navigator.push = guarded(push);
        navigator.replace = guarded(replace);

        return () => {
            navigator.push = push;
            navigator.replace = replace;
        };
    }, [navigator]);

    const proceed = useCallback(() => {
        held?.retry();
        setHeld(undefined);
    }, [held]);

    const stay = useCallback(() => setHeld(undefined), []);

    return { heldPath: held?.path, proceed, stay };
}
